import clsx from 'clsx';
import Button from 'components/Qds/Button';
import ExternalFunnel from 'components/Shared/ExternalFunnel/ExternalFunnel';
import ExternalFunnelButton from 'components/Shared/ExternalFunnel/ExternalFunnelButton';
import Image from 'components/ui/Image';
import config from 'config';
import { useTranslation } from 'hooks/useTranslation';
import { useEffect, useState } from 'react';
import { hideConversionBarUntilScroll } from 'utils/hideConversionBarUntilScroll';
import { useLivePreview } from 'hooks/useLivePreview';
import Divider from './Divider';
import styles from './HeroFunnel.module.scss';
import RichText from './RichText';
import type { InspectorPropsType } from 'hooks/useLivePreview';
import type {
  IBlockFunnelFields,
  IBlockHeroFunnelFields,
} from 'types/contentful/generated';
import type { ElementType } from 'react';
import type { ExternalFunnelProps } from 'components/Shared/ExternalFunnel/ExternalFunnel';

type CustomFunnelType = {
  __typename: string;
  sys: {
    id: string;
  };
  fields?: IBlockFunnelFields;
  fullWidth?: boolean;
  fullscreen?: boolean;
};

type Props = Omit<IBlockHeroFunnelFields, 'funnel' | 'secondaryFunnel'> & {
  funnel: CustomFunnelType;
  secondaryFunnel?: CustomFunnelType;
  inspectorProps: InspectorPropsType;
  sys: {
    id: string;
  };
  __typename: string;
};

const HeroFunnel = ({
  backgroundImage,
  backgroundImageMobile,
  descriptionAfterTitle,
  descriptionBeforeFunnel,
  funnelPosition,
  theme,
  title,
  titleHeadingLevel,
  funnel,
  introContentText,
  introContentTitle,
  trustImagesTitle,
  trustImages,
  disableAfterDescription,
  disableBeforeDescription,
  introPrimaryFunnelButton,
  secondaryFunnelButton,
  secondaryFunnel,
  useIntroScreen,
  ...props
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { updatedEntity, inspectorProps } = useLivePreview({
    sys: props?.sys,
    __typename: props?.__typename,
    backgroundImage,
  });

  const [isMobile, setIsMobile] = useState(false);

  const [selectedFunnel, setSelectedFunnel] = useState<CustomFunnelType | null>(
    null,
  );

  // eslint-disable-next-line
  // @ts-ignore
  const trustImagesItems = trustImages.items || trustImages;

  const hasImage = !!backgroundImage || !!backgroundImageMobile;

  const color = {
    light: hasImage ? 'text-dusty-chimney' : 'text-chicago',
    dark: 'text-white',
  };

  const bgColor = {
    light: 'bg-bluishGrey',
    dark: 'bg-green-vogue',
  };

  const themeClasses = {
    light: `${color.light} ${bgColor.light}`,
    dark: `${color.dark} ${bgColor.dark}`,
  };

  let bgImageSrcSmall = '';
  if (backgroundImageMobile) {
    bgImageSrcSmall = backgroundImageMobile.fields
      ? backgroundImageMobile.fields.file.url
      : // eslint-disable-next-line
        // @ts-ignore
        backgroundImageMobile.url;
  }

  let bgImageSrcBig = '';
  if (updatedEntity.backgroundImage) {
    bgImageSrcBig = updatedEntity.backgroundImage.fields
      ? updatedEntity.backgroundImage.fields.file.url
      : // eslint-disable-next-line
        // @ts-ignore
        backgroundImage.url;
  }

  if (funnel.fields?.fullWidth) {
    delete funnel.fields.fullWidth;
  } else {
    delete funnel.fullWidth;
  }

  const Heading = titleHeadingLevel.toLowerCase() as ElementType;

  const getFunnelId = (givenFunnel: CustomFunnelType): string => {
    return givenFunnel.sys.id;
  };

  const isFullscreenFunnel = (givenFunnel: CustomFunnelType): boolean => {
    return !!(givenFunnel.fullscreen || givenFunnel.fields?.fullscreen);
  };

  const sharedFunnelProps: Partial<ExternalFunnelProps> = {
    containerClasses: styles.funnelContainerClasses,
    useShadow: false,
    onInteraction: hideConversionBarUntilScroll,
  };

  const HeadingBlock = (): JSX.Element => (
    <div className="pb-8 max-w-xl md:px-8">
      {title && (
        <Heading
          className={clsx(
            'font-netto text-5xl mb-2 py-0 md:py-auto',
            color[theme],
          )}
        >
          {title}
        </Heading>
      )}
      {!disableAfterDescription && !!descriptionAfterTitle && (
        <RichText
          fields={descriptionAfterTitle}
          config={{
            paragraph: clsx(color[theme], 'mb-2'),
          }}
        />
      )}
    </div>
  );

  const FunnelIntro = ({
    bottomSpacing = true,
    showHeadline = false,
  }: {
    bottomSpacing?: boolean;
    showHeadline?: boolean;
  }): JSX.Element => {
    type RenderFunnelButton = (onClick: () => void) => JSX.Element;

    const renderPrimaryFunnelButton: RenderFunnelButton = (onClick) => {
      return (
        <Button className="mb-2" fullWidth onClick={onClick}>
          <span className="text-lg">
            {introPrimaryFunnelButton || t('getAQuote')}
          </span>
        </Button>
      );
    };

    const renderSecondaryFunnelButton: RenderFunnelButton = (onClick) => {
      return (
        <Button
          className="mb-2"
          variant="secondary"
          fullWidth
          onClick={onClick}
        >
          <span className="text-lg">{secondaryFunnelButton}</span>
        </Button>
      );
    };

    const renderButton = (
      givenFunnel: CustomFunnelType,
      renderFunnelButton: RenderFunnelButton,
    ): JSX.Element => {
      return isFullscreenFunnel(givenFunnel) ? (
        <ExternalFunnelButton
          {...sharedFunnelProps}
          funnelId={getFunnelId(givenFunnel)}
          renderButton={renderFunnelButton}
        />
      ) : (
        <>
          {renderFunnelButton(() => {
            setSelectedFunnel(givenFunnel);
          })}
          {/* preload the funnel as an optimization to reduce flashing */}
          <ExternalFunnel funnelId={getFunnelId(givenFunnel)} preload />
        </>
      );
    };

    return (
      <div>
        {/* // eslint-disable-next-line
                // @ts-ignore */}
        {config?.showDivider && <Divider />}
        {showHeadline ||
          !title ||
          // eslint-disable-next-line
          // @ts-ignore
          (config && config.showHeadline && (
            <h2 className="text-2xl mt-12 mb-8 md:text-center">{title}</h2>
          ))}
        <div
          className={clsx(
            styles.funnelIntroCard,
            'text-primary bg-white text-sm overflow-hidden rounded-bl-24p',
            bottomSpacing && 'mb-8',
          )}
          style={{ borderRadius: '2px 2px 2px 15px' }}
        >
          <div className="px-6 mx-auto pb-4 overflow-hidden md:overflow-visible flex flex-col bg-white grow-0">
            <div className="flex flex-col h-full">
              {introContentTitle && (
                <h3 className="font-bold font-adelle leading-9 pt-3 pb-4">
                  {introContentTitle}
                </h3>
              )}
              {introContentText && (
                <div className="text-lg md:text-base mb-5 h-full">
                  <RichText fields={introContentText} />
                </div>
              )}
              <div className="actions lg:pb-4">
                {renderButton(funnel, renderPrimaryFunnelButton)}

                {secondaryFunnel &&
                  secondaryFunnelButton &&
                  renderButton(secondaryFunnel, renderSecondaryFunnelButton)}

                {trustImagesTitle && (
                  <p className="text-xs text-center md:text-left mt-6 mb-4 text-qunoGrey">
                    {trustImagesTitle}
                  </p>
                )}
                {trustImages && (
                  <div className="flex items-center">
                    {trustImages &&
                      // eslint-disable-next-line
                      // @ts-ignore
                      trustImagesItems.map((img, i) => (
                        <img
                          key={i}
                          src={img.fields?.file.url || img.url}
                          alt={img.fields?.description || img.description}
                          className="mx-2"
                          style={{ maxWidth: '135px', height: 'auto' }}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  type FunnelBlockProps = { className?: string };

  const FunnelBlock = ({ className }: FunnelBlockProps): JSX.Element => {
    return (
      <div className={clsx(styles.funnelWrapper, 'mx-auto md:mx-0', className)}>
        {!disableBeforeDescription && !!descriptionBeforeFunnel && (
          <div className="bg-white md:bg-transparent px-5 rounded-t-sm md:rounded-none">
            <RichText
              fields={descriptionBeforeFunnel}
              config={{
                paragraph: clsx(
                  color[theme] === 'light'
                    ? styles.funnelBeforeDescriptionLight
                    : styles.funnelBeforeDescriptionDark,
                  'py-2',
                ),
              }}
            />
          </div>
        )}

        {useIntroScreen && !selectedFunnel ? (
          <FunnelIntro />
        ) : (
          <ExternalFunnel
            {...sharedFunnelProps}
            funnelId={getFunnelId(selectedFunnel || funnel)}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    setIsMobile(window.matchMedia('(max-width: 1023px)').matches);
  }, []);

  return (
    <>
      <div
        className={clsx(styles.bgImageSrc, themeClasses[theme], 'relative')}
        style={{ minHeight: '266px' }}
      >
        <div
          className="hidden md:block"
          {...inspectorProps({ fieldId: 'backgroundImage' })}
        >
          <Image
            src={bgImageSrcBig as string}
            alt="hero-funnel-image"
            fill
            priority
            className="object-cover"
          />
        </div>
        <div className="absolute md:hidden w-full h-full">
          <Image
            src={bgImageSrcSmall as string}
            alt="hero-funnel-image"
            fill
            priority
            className="object-cover"
          />
        </div>
        <div
          className={clsx(
            'relative flex items-center max-w-screen-xl mx-auto px-5 py-16 md:justify-between md:flex-nowrap md:px-4',
            funnelPosition === 'left' ? 'flex-wrap-reverse' : 'flex-wrap',
          )}
        >
          {!isMobile && (
            <FunnelBlock
              className={clsx(funnelPosition !== 'left' && 'order-last')}
            />
          )}
          <HeadingBlock />
        </div>
      </div>
      {isMobile && <FunnelBlock className="relative px-5" />}
    </>
  );
};

export default HeroFunnel;
